import React, { useState } from 'react';
import { Globe2, Plus, ExternalLink, Loader2, Building2, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Pill, MatchTypePill } from './badges';

const WebRow = ({ r, selected, onToggle, onAddCompany, adding, navigate }) => {
  const [open, setOpen] = useState(false);
  const added = !!r.added_company_id;
  return (
    <div className={`border-t border-slate-100 ${selected ? 'bg-sky-50/50' : 'bg-white'}`} data-testid={`web-row-${r.key}`}>
      <div className="flex items-start gap-3 px-3 py-2.5">
        <input type="checkbox" checked={selected} onChange={() => onToggle(r.key)} className="mt-1" data-testid={`web-select-${r.key}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-semibold text-slate-900">{r.company_name}</span>
            <span className="text-xs text-slate-500">{r.country || ''}</span>
            {r.website && <a href={r.website.startsWith('http') ? r.website : `https://${r.website}`} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline">site</a>}
            <MatchTypePill value={r.match_type} />
            {r.development_stage && r.development_stage !== 'Not identified' && <span className="text-[11px] text-slate-600">{r.development_stage}</span>}
            {added
              ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200"><Check className="w-3 h-3" /> Added to ForesightRx</span>
              : <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-emerald-600 text-white border-emerald-600">NEW — Not in ForesightRx</span>}
          </div>
          <p className="text-xs text-slate-700 mt-0.5 truncate">{r.relevant_product_platform}</p>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{r.rationale || r.evidence}</p>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {added ? (
            <button onClick={() => navigate(`/biotech/${r.added_company_id}`)} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50" data-testid={`web-view-${r.key}`}><Building2 className="w-3 h-3" /> View · Enrich</button>
          ) : (
            <button onClick={() => onAddCompany(r.key)} disabled={adding?.has(r.key)} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50" data-testid={`web-add-${r.key}`}>
              {adding?.has(r.key) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add Company
            </button>
          )}
          <button onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100" data-testid={`web-toggle-${r.key}`}>{open ? 'Less' : 'Details'} {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</button>
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3 pl-10 space-y-1.5 text-xs">
          <div className="flex flex-wrap gap-x-6 gap-y-1"><span><span className="text-slate-500">Relevance </span><Pill value={r.relevance} /></span><span><span className="text-slate-500">Manufacturing </span><Pill value={r.manufacturing} /></span><span><span className="text-slate-500">Partnering </span><Pill value={r.partnering} /></span></div>
          {r.evidence && <p className="italic text-slate-700">“{r.evidence}”</p>}
          {r.source_urls?.length > 0 && <div className="flex flex-wrap gap-1">{r.source_urls.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[10px] text-sky-700 hover:underline max-w-[240px] truncate"><ExternalLink className="w-2.5 h-2.5" />{u.replace(/^https?:\/\//, '')}</a>)}</div>}
          <p className="text-[10px] text-slate-400">Web source — unverified. Add the company to enrich it inside ForesightRx.</p>
        </div>
      )}
    </div>
  );
};

export const WebResults = ({ rows, selected, onToggle, onAddCompany, adding, navigate, loading }) => {
  if (loading) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 flex items-center gap-2 text-xs text-slate-600" data-testid="web-expand-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching the web for companies not yet in ForesightRx — usually 30–90s…
      </div>
    );
  }
  if (!rows || rows.length === 0) return null;
  const fresh = rows.filter(r => !r.in_crm);
  const known = rows.filter(r => r.in_crm);
  return (
    <div className="rounded-lg border border-emerald-200 bg-white overflow-hidden" data-testid="web-results">
      <div className="px-3 py-1.5 bg-emerald-50 flex items-center gap-2">
        <Globe2 className="w-3.5 h-3.5 text-emerald-700" />
        <span className="text-xs font-bold text-emerald-900">Web discovery — {fresh.length} NEW companies not currently in ForesightRx</span>
        <span className="ml-auto text-[10px] text-emerald-800">Unverified · review before adding</span>
      </div>
      {fresh.map(r => <WebRow key={r.key} r={r} selected={selected.has(r.key)} onToggle={onToggle} onAddCompany={onAddCompany} adding={adding} navigate={navigate} />)}
      {known.length > 0 && (
        <div className="px-3 py-1.5 border-t border-slate-100 text-[10px] text-slate-500">Also surfaced but already in ForesightRx: {known.map(k => k.company_name).join(', ')}</div>
      )}
    </div>
  );
};
